import { Injectable } from '@angular/core';
import { IMerchantPartnersFormData } from './dialog-merchant-partners-interface';

export interface ValidationError {
    field: string;
    message: string;
}

@Injectable({
    providedIn: 'root'
})
export class MerchantValidatorService {

    validate(data: IMerchantPartnersFormData, partnerData: any): ValidationError[] {
        const errors: ValidationError[] = [];

        // Validar dados gerais
        this.validateGeneralData(partnerData, errors);

        // Validar dados financeiros
        this.validateFinancialData(data, errors);

        // Validar dados do responsável
        this.validateResponsibleData(data, errors);

        // Validar configuração
        this.validateConfiguration(data, errors);

        // Validar ID externo e tipo de conta
        this.validateExternalId(data.idExterno, errors);
        this.validateAccountType(data.tipoConta, errors);

        return errors;
    }

    private validateGeneralData(partnerData: any, errors: ValidationError[]): void {
        // Validar documento (CPF/CNPJ)
        if (partnerData.cpf) {
            const cpf = partnerData.cpf.replace(/\D/g, '');
            if (!this.isValidCPF(cpf)) {
                errors.push({ field: 'CPF', message: 'CPF inválido' });
            }
        } else if (partnerData.cnpj) {
            const cnpj = partnerData.cnpj.replace(/\D/g, '');
            if (!this.isValidCNPJ(cnpj)) {
                errors.push({ field: 'CNPJ', message: 'CNPJ inválido' });
            }
        } else {
            errors.push({ field: 'Documento', message: 'CPF ou CNPJ é obrigatório' });
        }

        // Validar nome (PF usa 'name', PJ usa 'companyName')
        const hasName = partnerData.name && String(partnerData.name).trim();
        const hasCompanyName = partnerData.companyName && String(partnerData.companyName).trim();

        if (!hasName && !hasCompanyName) {
            errors.push({ field: 'Nome/Razão Social', message: 'Nome (PF) ou Razão Social (PJ) é obrigatório' });
        }

        // Validar data de nascimento
        if (!partnerData.birthday) {
            errors.push({ field: 'Data de Nascimento', message: 'Data de nascimento é obrigatória' });
        }

        // Validar e-mail
        if (!this.isValidEmail(partnerData.email)) {
            errors.push({ field: 'E-mail', message: 'E-mail inválido' });
        }

        // Validar endereço
        this.validateAddress(partnerData, 'Endereço do Parceiro', errors);
    }

    private validateFinancialData(data: IMerchantPartnersFormData, errors: ValidationError[]): void {
        if (data.chavePix?.trim() && !this.isValidPixKey(data.chavePix.trim())) {
            errors.push({ field: 'Chave PIX', message: 'Chave PIX inválida. Deve ser um CPF, CNPJ, e-mail, telefone ou Chave Aleatória.' });
        }

        const celular = data.celularFinanceiro?.replace(/\D/g, '');
        if (!celular || celular.length !== 11) {
            errors.push({ field: 'Celular Financeiro', message: 'Celular financeiro deve ter 11 dígitos (DDD + número)' });
        }

        if (!this.isValidEmail(data.emailFinanceiro)) {
            errors.push({ field: 'E-mail Financeiro', message: 'E-mail financeiro inválido' });
        }
    }

    private isValidPixKey(key: string): boolean {
        return (
            this.isValidCpf(key) ||
            this.isValidCnpj(key) ||
            this.isValidEmail(key) ||
            this.isValidPhoneNumber(key) ||
            this.isValidUuid(key)
        );
    }

    // Valida CPF (11 dígitos)
    private isValidCpf(cpf: string): boolean {
        if (!cpf || typeof cpf !== 'string') return false;

        const cleanCpf = cpf.replace(/\D/g, '');
        return /^\d{11}$/.test(cleanCpf);
    }

    // Valida CNPJ (14 dígitos)
    private isValidCnpj(cnpj: string): boolean {
        if (!cnpj || typeof cnpj !== 'string') return false;

        const cleanCnpj = cnpj.replace(/\D/g, '');
        return /^\d{14}$/.test(cleanCnpj);
    }

    // Valida telefone brasileiro (apenas números, considerando 11 dígitos)
    private isValidPhoneNumber(phone: string): boolean {
        if (!phone || typeof phone !== 'string') return false;

        const cleanPhone = phone.replace(/\D/g, '');

        let phoneWithoutCountryCode = cleanPhone;
        if (cleanPhone.startsWith('55') && cleanPhone.length === 13) {
            phoneWithoutCountryCode = cleanPhone.substring(2);
        }

        return /^\d{10,11}$/.test(phoneWithoutCountryCode);
    }

    // Valida UUID
    private isValidUuid(uuid: string): boolean {
       const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
    }

    private validateResponsibleData(data: IMerchantPartnersFormData, errors: ValidationError[]): void {
        // Nome do responsável
        if (!data.responsavelNome?.trim()) {
            errors.push({ field: 'Nome do Responsável', message: 'Nome do responsável é obrigatório' });
        }

        // CPF do responsável
        const cpf = data.responsavelCpf?.replace(/\D/g, '');
        if (!this.isValidCPF(cpf)) {
            errors.push({ field: 'CPF do Responsável', message: 'CPF do responsável inválido' });
        }

        // RG do responsável
        if (!data.responsavelRg) {
            errors.push({ field: 'RG do Responsável', message: 'RG do responsável é obrigatório' });
        }

        // Nacionalidade
        if (!data.responsavelNacionalidade?.trim()) {
            errors.push({ field: 'Nacionalidade', message: 'Nacionalidade é obrigatória' });
        }

        // Celular do responsável
        const celular = data.responsavelCelular?.replace(/\D/g, '');
        if (!celular || celular.length < 11) {
            errors.push({ field: 'Celular do Responsável', message: 'Celular deve ter 11 dígitos (DDD + número)' });
        }

        // E-mail do responsável
        if (!this.isValidEmail(data.responsavelEmail)) {
            errors.push({ field: 'E-mail do Responsável', message: 'E-mail do responsável inválido' });
        }

        // Data de nascimento
        if (!data.responsavelDataNascimento) {
            errors.push({ field: 'Data de Nascimento do Responsável', message: 'Data de nascimento do responsável é obrigatória' });
        }

        // Validar endereço do responsável
        this.validateResponsibleAddress(data, errors);
    }

    private validateAddress(data: any, prefix: string, errors: ValidationError[]): void {
        const cep = data.cep?.replace(/\D/g, '');
        if (!cep || cep.length !== 8) {
            errors.push({ field: `CEP - ${prefix}`, message: 'CEP deve ter 8 dígitos' });
        }

        const state = String(data.state ?? '');
        if (!state.trim() || state.length !== 2) {
            errors.push({ field: `Estado - ${prefix}`, message: 'Estado deve ter 2 caracteres' });
        }

        const city = String(data.city ?? '');
        if (!city.trim()) {
            errors.push({ field: `Cidade - ${prefix}`, message: 'Cidade é obrigatória' });
        }

        const address = String(data.address ?? '');
        if (!address.trim()) {
            errors.push({ field: `Endereço - ${prefix}`, message: 'Endereço é obrigatório' });
        }

        // Usa ?? em vez de || para aceitar 0 como valor válido
        const addressNumber = data.addressNumber != null ? String(data.addressNumber) : '';
        if (!addressNumber.trim()) {
            errors.push({ field: `Número - ${prefix}`, message: 'Número é obrigatório' });
        }

        const neighborhood = String(data.neighborhood ?? '');
        if (!neighborhood.trim()) {
            errors.push({ field: `Bairro - ${prefix}`, message: 'Bairro é obrigatório' });
        }
    }

    private validateResponsibleAddress(data: IMerchantPartnersFormData, errors: ValidationError[]): void {
        const cep = data.responsavelCep?.replace(/\D/g, '');
        if (!cep || cep.length !== 8) {
            errors.push({ field: 'CEP do Responsável', message: 'CEP deve ter 8 dígitos' });
        }

        const estado = String(data.responsavelEstado ?? '');
        if (!estado.trim() || estado.length !== 2) {
            errors.push({ field: 'Estado do Responsável', message: 'Estado deve ter 2 caracteres' });
        }

        const cidade = String(data.responsavelCidade ?? '');
        if (!cidade.trim()) {
            errors.push({ field: 'Cidade do Responsável', message: 'Cidade é obrigatória' });
        }

        const endereco = String(data.responsavelEndereco ?? '');
        if (!endereco.trim()) {
            errors.push({ field: 'Endereço do Responsável', message: 'Endereço é obrigatório' });
        }

        const numero = data.responsavelNumero != null ? String(data.responsavelNumero) : '';
        if (!numero.trim()) {
            errors.push({ field: 'Número do Responsável', message: 'Número é obrigatório' });
        }

        const bairro = String(data.responsavelBairro ?? '');
        if (!bairro.trim()) {
            errors.push({ field: 'Bairro do Responsável', message: 'Bairro é obrigatório' });
        }
    }

    private validateConfiguration(data: IMerchantPartnersFormData, errors: ValidationError[]): void {
        if (data.validarIp) {
            if (!data.ips?.trim()) {
                errors.push({ field: 'IPs', message: 'Lista de IPs é obrigatória quando validação de IP está ativada' });
            } else {
                const ips = data.ips.split(',').map(ip => ip.trim());
                for (const ip of ips) {
                    if (!this.isValidIPv4(ip)) {
                        errors.push({ field: 'IPs', message: `IP inválido: "${ip}". Deve ser um endereço IPv4 válido` });
                    }
                }
            }
        }
    }

    private validateExternalId(idExterno: string, errors: ValidationError[]): void {
        if (!idExterno?.trim()) {
            errors.push({ field: 'ID Externo', message: 'ID externo é obrigatório' });
        }
    }

    private validateAccountType(tipoConta: any, errors: ValidationError[]): void {
        const validTypes = ['liquidacao', 'pagamento'];
        const accountType = typeof tipoConta === 'object' ? tipoConta?.value : tipoConta;

        if (!validTypes.includes(accountType)) {
            errors.push({ field: 'Tipo de Conta', message: 'Tipo de conta deve ser "liquidacao" ou "pagamento"' });
        }
    }

    // Validadores auxiliares
    private isValidEmail(email: string): boolean {
        if (!email) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    private isValidCPF(cpf: string): boolean {
        if (!cpf) return false;
        cpf = cpf.replace(/\D/g, '');

        if (cpf.length !== 11) return false;

        // Verifica se todos os dígitos são iguais
        if (/^(\d)\1+$/.test(cpf)) return false;

        // Validação do primeiro dígito verificador
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cpf.charAt(i)) * (10 - i);
        }
        let digit = 11 - (sum % 11);
        if (digit >= 10) digit = 0;
        if (digit !== parseInt(cpf.charAt(9))) return false;

        // Validação do segundo dígito verificador
        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cpf.charAt(i)) * (11 - i);
        }
        digit = 11 - (sum % 11);
        if (digit >= 10) digit = 0;
        if (digit !== parseInt(cpf.charAt(10))) return false;

        return true;
    }

    private isValidCNPJ(cnpj: string): boolean {
        if (!cnpj) return false;
        cnpj = cnpj.replace(/\D/g, '');

        if (cnpj.length !== 14) return false;

        // Verifica se todos os dígitos são iguais
        if (/^(\d)\1+$/.test(cnpj)) return false;

        // Validação do primeiro dígito verificador
        let sum = 0;
        let weight = 5;
        for (let i = 0; i < 12; i++) {
            sum += parseInt(cnpj.charAt(i)) * weight;
            weight = weight === 2 ? 9 : weight - 1;
        }
        let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
        if (digit !== parseInt(cnpj.charAt(12))) return false;

        // Validação do segundo dígito verificador
        sum = 0;
        weight = 6;
        for (let i = 0; i < 13; i++) {
            sum += parseInt(cnpj.charAt(i)) * weight;
            weight = weight === 2 ? 9 : weight - 1;
        }
        digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
        if (digit !== parseInt(cnpj.charAt(13))) return false;

        return true;
    }

    private isValidIPv4(ip: string): boolean {
        if (!ip) return false;
        const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (!ipRegex.test(ip)) return false;

        const parts = ip.split('.');
        return parts.every(part => {
            const num = parseInt(part, 10);
            return num >= 0 && num <= 255;
        });
    }
}

